import { cn } from '@/lib/utils';

/** Palette d'avatars de la maquette — assignée par membre, stable dans le temps. */
export const AVATAR_COLORS = {
  br: 'var(--color-av-br)',
  gd: 'var(--color-av-gd)',
  nv: 'var(--color-av-nv)',
  tl: 'var(--color-av-tl)',
  pl: 'var(--color-av-pl)',
  rd: 'var(--color-av-rd)',
  br2: 'var(--color-av-br2)',
} as const;

export type AvatarColor = keyof typeof AVATAR_COLORS;

export const AVATAR_COLOR_KEYS = Object.keys(AVATAR_COLORS) as AvatarColor[];

/** Initiales d'un nom complet : « Awa Koné » → « AK ». */
export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Couleur déterministe à partir du nom, quand aucune n'est enregistrée en base. */
export function avatarColorFor(name: string): AvatarColor {
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i);
  return AVATAR_COLOR_KEYS[sum % AVATAR_COLOR_KEYS.length] as AvatarColor;
}

export type AvatarProps = {
  name: string;
  /** Diamètre en px. 40 par défaut ; 42 dans la barre du haut, 56 dans les tiroirs. */
  size?: number;
  color?: AvatarColor;
  /** Photo de profil. Les initiales restent dessous et réapparaissent si elle ne charge pas. */
  src?: string | null;
  className?: string;
};

export function Avatar({ name, size = 40, color, src, className }: AvatarProps) {
  const key = color ?? avatarColorFor(name);
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full font-display font-bold text-white',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        background: AVATAR_COLORS[key],
      }}
      title={name}
    >
      {initials(name)}
      {src ? (
        // Superposée plutôt que substituée : si l'URL casse — lien Cloudinary
        // expiré, réseau coupé au marché — les initiales colorées réapparaissent
        // d'elles-mêmes. `alt=""` la rend décorative, donc rien ne s'affiche à sa
        // place en cas d'échec, et le lecteur d'écran ne lit pas le nom deux fois.
        //
        // Balise `<img>` et non `next/image` : la photo vient d'un hôte distant
        // arbitraire, et l'optimiseur de Next exigerait de déclarer ce domaine
        // dans `next.config`. Pour une vignette de 40 px, le réglage coûte plus
        // qu'il ne rapporte.
        <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : null}
    </div>
  );
}
